<template> 
  <b-container  
    fluid
  >
    <b-form-row class="mb-2 align-items-center">
      <b-col cols="auto">
        <b-input 
          v-model="search" 
          type="search"
          placeholder="Zoeken in units van relatie" 
        />
      </b-col>
      <b-col cols="auto">
        <b-form-checkbox
          v-model="toonUitContract"
          switch
        >
          Toon uit contract
        </b-form-checkbox>
      </b-col>
      <b-col cols="auto">
        <small class="text-light">{{ filteredUnits.length }} / {{ units.length }} units</small>
      </b-col>
    </b-form-row>
     
    <b-table
      ref="selectableTable"
      :items="filteredUnits"
      :filter="search"
      :filter-included-fields="['Titel','Adres','Postcode','WijkPlaats','UnitKenmerk','Onderhoudsfirma.Naam','Onderhoudsnummer']"
      :busy="busy"
      :fields="[ 
        { key:'UnitKenmerk', label:'Unitkenmerk' },
        { key:'Adres', label:'Adres' },
        { key:'HuidigeStatus', label:'Status' },
        { key:'LaatsteInspectie', label:'Laatste inspectie' },
        { key:'Instellingen.klantnummer', label:'Klantnr' },
        { key:'Instellingen.soortOnderhoud', label:'SoortOH' },
        { key:'Bedragen', label:'C / P / K / S', class: 'text-center' },
        { key:'Onderhoud', label:'Onderhoud' },
        { key:'Keuring', label:'Keuring' },
        { key:'Keuringsstatus', label:'Certificaat' }
      ]"

      striped
      bordered
      small
      selectable
      responsive
      hover
      @row-selected="onRowSelected($event)"
    >
      <template 
        slot="thead-top" 
        slot-scope="data"
      >
        <tr>
          <th colspan="4">
            Unit
          </th>
          <th colspan="3">
            Prognose
          </th>
          <th />
          <th colspan="2">
            Keuring
          </th>
        </tr>
      </template>

      <template #cell(Keuringsstatus)="data">
        <keurings-status 
          :status="data.item.KeuringsStatus" 
          :deadline="new Date(data.item.Keuringsdeadline)"
        />
      </template>

      <template #cell="data">
        {{ data.item[data.key] }}
      </template>

      <template #cell(Adres)="data">
        {{ data.item.Adres }}<br>
        <small class="text-light">{{ data.item.Postcode }} {{ data.item.WijkPlaats }}</small>
      </template>
      
      <template #table-busy>
        <div class="text-center text-white my-2">
          <b-spinner class="align-middle mx-2" />
          <strong>Laden...</strong>
        </div>
      </template>

      <template #cell(UnitKenmerk)="row">
        <b-link :to="{name:'Algemeen',params:{ id: row.item.Id}}">
          {{ row.item.UnitKenmerk }}
        </b-link><br>
        <small class="text-light">{{ row.item.Titel }}</small>
      </template>

      <template #cell(LaatsteInspectie)="row">
        <template v-if="row.item.LaatsteInspectieDatum">
          {{ moment(row.item.LaatsteInspectieDatum).format('DD-MM-YYYY') }}
          <br>
          <b-badge
            v-if="row.item.HuidigeStatus == 1 && maandenSindsInspectie(row.item) >= 33"
            :variant="maandenSindsInspectie(row.item) >= 36 ? 'danger' : 'warning'"
            pill
          >
            {{ maandenSindsInspectie(row.item) }} mnd.
          </b-badge>
          <small 
            v-else 
            class="text-light"
          >
            {{ maandenSindsInspectie(row.item) }} mnd.
          </small>
        </template>
        <b-badge
          v-else
          variant="secondary"
          pill
        >
          Geen
        </b-badge>
      </template>

      <template #cell(Bedragen)="row">
        {{ row.item.Instellingen.standaardCorrectief || relatie.Config.standaardCorrectief }}
        / {{ row.item.Instellingen.standaardPreventief || relatie.Config.standaardPreventief }}
        / {{ row.item.Instellingen.standaardKeuringskosten || relatie.Config.standaardKeuringskosten }}
        / {{ row.item.Instellingen.standaardSpreekluister || relatie.Config.standaardSpreekluister }}
      </template>

      <template #cell(Onderhoud)="row">
        {{ row.item.Onderhoudsfirma.Naam }}<br>
        <small class="text-light">{{ row.item.Onderhoudsnummer }}</small>
      </template>

      <template #cell(Keuring)="row">
        {{ row.item.Keuringsinstantie }}<br>
        <small class="text-light">{{ row.item.Keuringsnummer }}</small>
      </template>

      <template #cell(HuidigeStatus)="row">
        {{ statusLabel(row.item.HuidigeStatus) }} 
      </template>
      
      <template #row-details="row">
        <b-button-toolbar>
          <b-button 
            :to="{name:'Algemeen',params:{ id: row.item.Id}}" 
            size="sm"
          >
            <i class="cui-dashboard" /> Algemeen
          </b-button>
          <b-button 
            :to="{name:'Inventarisatie',params:{ id: row.item.Id}}" 
            class="mx-2" 
            size="sm"
          >
            <i class="cui-list" /> Inventarisatie
          </b-button>
          <b-button 
            :to="{name:'Prognoses',params:{ id: row.item.Id}}" 
            size="sm"
          >
            <i class="cui-cog" /> Prognoses
          </b-button>
        </b-button-toolbar>
      </template>
    </b-table>
    <b-modal 
      v-if="selectedUnit" 
      v-model="showQuickEdit"
      :busy="busy" 
      :title="`Unit ${selectedUnit.UnitKenmerk}`"
      @ok="saveUnit"
    >
      <b-form-group 
        label="Snelmenu" 
        class="py-2"
      >
        <b-button-toolbar>
          <b-button 
            :to="{name:'Algemeen',params:{ id: editUnit.Id}}" 
            size="sm"
          >
            <i class="cui-dashboard" /> Algemeen
          </b-button>
          <b-button 
            :to="{name:'Inventarisatie',params:{ id: editUnit.Id}}" 
            class="mx-2" 
            size="sm"
          >
            <i class="cui-list" /> Inventarisatie
          </b-button>
          <b-button 
            :to="{name:'Prognoses',params:{ id: editUnit.Id}}" 
            size="sm"
          >
            <i class="cui-cog" /> Prognoses
          </b-button>
          <b-button 
            size="sm"
            class="mx-2" 
            @click="() => publishUnit(editUnit.Id)"
          >
            <font-awesome-icon icon="export" />
            Publiseren
          </b-button>
        </b-button-toolbar>
      </b-form-group>
      <b-form-group label="Status">
        <unit-status 
          :status="editUnit.HuidigeStatus" 
          @status-changed="editUnit.HuidigeStatus = $event"
        />
      </b-form-group>

      <b-form-group label="Titel">
        <b-input v-model="editUnit.Titel" />
      </b-form-group>

      <b-form-group label="Adres">
        <b-input v-model="editUnit.Adres" />   
      </b-form-group>

      <b-form-group label="Postcode">
        <b-input-group>
          <b-input v-model="editUnit.Postcode" />
          <b-input v-model="editUnit.WijkPlaats" />
        </b-input-group>
      </b-form-group>
      
      
      <b-form-group 
        label="Klantnummer" 
        description="Nummer van de klant voor de installatie"
      >
        <b-input v-model="editUnit.Instellingen.klantnummer" />
      </b-form-group>
      
      <b-form-group label="SoortOnderhoud">
        <b-input v-model="editUnit.Instellingen.soortOnderhoud" />
      </b-form-group>
      <b-form-row>
        <b-form-text>
          Wijzig de standaarden in de configuratie van de relatie om de standaarden te wijzigen.
          <b-link :to="{name: 'RelatieEdit'}">
            Klik hier
          </b-link>
        </b-form-text>
        <b-col cols="3">
          <b-form-group label="Correctief">
            <b-input 
              v-model.number="editUnit.Instellingen.standaardCorrectief" 
              :placeholder="relatie.Config.standaardCorrectief.toString()" 
              type="number"
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group label="Preventief">
            <b-input 
              v-model.number="editUnit.Instellingen.standaardPreventief"
              :placeholder="relatie.Config.standaardPreventief.toString()" 
              type="number"
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group label="Keuringskosten">
            <b-input 
              v-model.number="editUnit.Instellingen.standaardKeuringskosten"
              :placeholder="relatie.Config.standaardKeuringskosten.toString()"
              type="number"
            />
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group label="Spreekluister">
            <b-input 
              v-model.number="editUnit.Instellingen.standaardSpreekluister"
              :placeholder="relatie.Config.standaardSpreekluister.toString()" 
              type="number"
            />
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-group 
        
        label="Onderhoudsfirma"
      >
        <b-input-group>
          <b-select
            :options="onderhoudsfirmas"
            :value="editUnit.Onderhoudsfirma ? editUnit.Onderhoudsfirma.Id : null"
            text-field="Naam"

            value-field="Id"
            @input="editUnit.Onderhoudsfirma = $event"
          >
            <template #first>
              <option 
                value 
                disabled
              >
                -- Kies een onderhoudsfirma --
              </option>
            </template>
          </b-select>
          <b-input v-model="editUnit.Onderhoudsnummer" />
        </b-input-group>
      </b-form-group>

      <b-form-group label="Keuringsinstantie">
        <b-input-group>
          <b-select
            v-model="editUnit.Keuringsinstantie"
            :options="keuringsinstanties"
          >
            <template #first>
              <option value="">Geen</option>
            </template>
          </b-select>
          <b-input v-model="editUnit.Keuringsnummer" />
        </b-input-group>
      </b-form-group>
    </b-modal>
  </b-container>
</template>
<script>
import { mapGetters } from 'vuex';
import { serviceMixin } from "@/mixins/serviceMixin";
import FontAwesome from "../icons/FontAwesome.vue";
import UnitStatus from '../../components/UnitStatus.vue';
import KeuringsStatus from '../../components/KeuringsStatus.vue';
import moment from 'moment';

export default {
  components: {
    FontAwesome,
    UnitStatus,
    KeuringsStatus
  },
  mixins: [serviceMixin],
  props: {
    relatie: {
      type: Object,
      default: () => null,
    },
    relatieId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      search: null,
      toonUitContract: false,
      units: [],
      busy: false,
      selectedUnit: null,
      showQuickEdit: false,
      onderhoudsfirmas: [],
    };
  },
  computed: {
    ...mapGetters('relaties', ['keuringsinstanties']),
    filteredUnits: function () {
      if (this.toonUitContract) return this.units;
      return this.units.filter(u => u.HuidigeStatus != 0);
    },
    editUnit: function () {
      return { ...this.selectedUnit, Instellingen: { ...this.selectedUnit.Instellingen } };
    },
  },
  mounted() {
    this.$store.dispatch('relaties/refreshIfStale');
    this.getUnits();
    this.getOnderhoudsfirmas();
  },
  methods: {
    getUnits() {
      this.busy = true;
      const id = this.relatieId || this.$route.params.id;
      this.get(`/relaties/${id}/units/overzicht`)
        .then((response) => {
          this.units = response.data.units;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onRowSelected(items) {
      if (items.length == 1) {
        this.selectedUnit = items[0];
        this.showQuickEdit = true;
        this.clearSelected();
      }
    },
    publishUnit(unitid) {
      this.get(`/unit/${unitid}/publish`)
        .then(function (response) {
          console.dir(response.data);
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    getOnderhoudsfirmas() {
      var vm = this;
      this.get("/onderhoud/relaties")
        .then(function (response) {
          console.dir(response.data);
          vm.onderhoudsfirmas = response.data.relaties;
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    onStatusChanged(status) {
      this.editUnit.HuidigeStatus = status;
    },
    saveUnit() {
      var vm = this;
      this.busy = true;

      if (this.editUnit.Onderhoudsfirma !== null) {
        if (!isNaN(this.editUnit.Onderhoudsfirma.Id))
          this.editUnit.Onderhoudsfirma = this.editUnit.Onderhoudsfirma.Id;
      }
      
      if (this.editUnit.HuidigeStatus < 2) this.editUnit.Keuringsalarm = this.editUnit.HuidigeStatus;

      var edited = this.editUnit;
      this.post("/unit/" + this.selectedUnit.Id, edited)
        .then(function () {
          vm.$set(vm.selectedUnit, "Titel", edited.Titel);
          vm.$set(vm.selectedUnit, "Instellingen", edited.Instellingen);
          vm.$set(vm.selectedUnit, "Adres", edited.Adres);
          vm.$set(vm.selectedUnit, "Postcode", edited.Postcode);
          vm.$set(vm.selectedUnit, "WijkPlaats", edited.WijkPlaats);
          vm.$set(vm.selectedUnit, "HuidigeStatus", edited.HuidigeStatus);
          vm.$set(vm.selectedUnit, "Keuringsnummer", edited.Keuringsnummer);
          vm.$set(vm.selectedUnit, "Keuringsinstantie", edited.Keuringsinstantie);
          vm.$set(vm.selectedUnit, "Onderhoudsnummer", edited.Onderhoudsnummer);
          // Reconstruct the Onderhoudsfirma struct for the combined cell
          if (edited.Onderhoudsfirma && !isNaN(edited.Onderhoudsfirma)) {
            var firma = vm.onderhoudsfirmas.find(f => f.Id == edited.Onderhoudsfirma);
            vm.$set(vm.selectedUnit, "Onderhoudsfirma", firma ? { Id: firma.Id, Naam: firma.Naam } : vm.selectedUnit.Onderhoudsfirma);
          }
        })
        .catch(function (error) {
          alert(error);
        })
        .finally(function () {
          vm.busy = false;
        });
    },
    maandenSindsInspectie(unit) {
      if (!unit.LaatsteInspectieDatum) return null;
      return moment().diff(moment(unit.LaatsteInspectieDatum), 'months');
    },
    statusLabel(status) {
      var statusOpties= [
        { label: "Uit contract" },
        { label: "In beheer" },
        { label: "Subbeheer" },
        { label: "Losse inspectie" }
      ];
      return statusOpties[status].label;
    },
    clearSelected() {
      this.$refs.selectableTable.clearSelected();
    },
  },
};
</script>
